import {
	convertCandidateStateToLegacy,
	convertHttpErrorToRunErrorCode,
	convertSelectionToRequest,
} from "@/components/Project/road-ai/roadAiAdapter";
import { postRoadAiRouteProposal } from "@/components/Project/road-ai/roadAiApiClient";
import { pollRoadAiJob } from "@/components/Project/road-ai/roadAiPolling";
import { convertCoordinatesToCesiumCrs } from "./cesium-common";

/**
 * 仮設道路AI（D3D-774）の実行ユーティリティ。
 * 実行導線は v2-server 経由（受付 → 3 秒間隔ポーリング）で、ai-road を直接叩かない。
 * 使用する現況・設計データは表示中アセットからサーバー側が取得する。
 */

/**
 * 実行中止を表すエラー。呼び出し元は code から文言を決める（buildRunErrorMessage）。
 */
export class RoadAiRunError extends Error {
	/**
	 * @param {string} code RoadAiRunErrorCode
	 */
	constructor(code) {
		super(code);
		this.name = "RoadAiRunError";
		this.code = code;
	}
}

/**
 * 仮設道路AI を実行し候補経路を取得する（受付 → ポーリング）。
 *
 * @param {Object} params
 * @param {string} params.siteId 現場 UUID
 * @param {import("@/components/Project/road-ai/roadAi.types").RoadAiDataSelection} params.selection 使用データ（検査で確定したもの）
 * @param {number[]} params.start 始点 [x, y, z]（point-cloud CRS）
 * @param {number[]} params.end 終点 [x, y, z]
 * @param {number} params.roadWidthM 道路幅員 [m]
 * @param {number} params.slopeRatio 法面勾配比
 * @param {number} params.maxSlopePercent 最大縦断勾配 [%]
 * @param {string[]} params.objectives 目的関数の配列
 * @param {string} [params.cfDiffEvaluator] cf_diff 評価方式
 * @param {number} [params.topKPerObjective] 目的ごとの候補数
 * @param {string} [params.searchBackend] 探索バックエンド
 * @param {number} [params.objectiveWeight] 目的項の重み
 * @param {number[][]} [params.waypoints] 順序付き中継点 [[x,y,z], …]（0..5）
 * @param {number} [params.waypointToleranceM] 中継点の通過半径 [m]（一律, 既定 5.0）
 * @returns {Promise<{status: string, candidates: Array, candidateState: import("@/components/Project/road-ai/roadAi.types").RoadAiCandidateState, diagnostics: Array, usedSource: Object, mode: string}>}
 */
export const runPrototypeRoadAI = async (params) => {
	const body = convertSelectionToRequest(params.selection, {
		start: params.start,
		end: params.end,
		waypoints: params.waypoints || [],
		roadWidthM: params.roadWidthM,
		maxSlopePercent: params.maxSlopePercent,
		objectives: params.objectives,
		slopeRatio: params.slopeRatio,
		waypointToleranceM: params.waypointToleranceM,
		cfDiffEvaluator: params.cfDiffEvaluator,
		topKPerObjective: params.topKPerObjective,
		searchBackend: params.searchBackend,
		objectiveWeight: params.objectiveWeight,
	});

	let accepted;
	try {
		const response = await postRoadAiRouteProposal(params.siteId, body);
		const json = await response.json().catch(() => null);
		if (!response.ok) {
			throw new RoadAiRunError(
				convertHttpErrorToRunErrorCode(response.status, json),
			);
		}
		// ok 応答でも本文が壊れている（JSON でない・jobHandle 欠落）場合は実行失敗に畳む。
		if (!json || typeof json.jobHandle !== "string") {
			throw new RoadAiRunError("ROUTE_ENGINE_FAILED");
		}
		accepted = json;
	} catch (e) {
		if (e instanceof RoadAiRunError) throw e;
		console.error("Failed to submit road AI job", e);
		throw new RoadAiRunError("NETWORK_ERROR");
	}

	// 受付完了。以降はジョブ照会が決着するまで待つ（上限時間・中止操作は設けない）。
	const progress = await pollRoadAiJob(params.siteId, accepted.jobHandle);
	if (progress.status === "failed") {
		throw new RoadAiRunError(progress.errorCode);
	}

	return {
		status: "ok",
		// 戻り値は既存表示（提案経路一覧・PoC ダイアログ）互換の snake_case を維持しつつ、
		// 正規化状態もそのまま返す（store へは再正規化せずこちらを保存する）。
		candidates: convertCandidateStateToLegacy(progress.candidateState),
		candidateState: progress.candidateState,
		diagnostics: progress.diagnostics,
		usedSource: accepted.usedSource,
		mode: accepted.mode,
	};
};

/**
 * フラット座標配列 [x, y, z, ...] を Cesium 座標系へ変換する。
 *
 * @param {number[]} pointsFlat プロジェクト座標のフラット配列
 * @returns {Promise<number[]>} 変換後のフラット配列
 */
const convertPointsFlat = async (pointsFlat) => {
	if (!Array.isArray(pointsFlat) || pointsFlat.length % 3 !== 0) {
		throw new Error("pointsは3の倍数の長さの配列である必要があります");
	}
	const coords = [];
	for (let i = 0; i < pointsFlat.length; i += 3) {
		coords.push({
			x: pointsFlat[i],
			y: pointsFlat[i + 1],
			z: pointsFlat[i + 2],
		});
	}
	// バッチ変換で worker 往復を 1 回に抑える（単点版の NaN フォールバックも避ける）。
	const converted = await convertCoordinatesToCesiumCrs(coords);
	if (!Array.isArray(converted) || converted.length !== coords.length) {
		throw new Error("座標変換に失敗しました。もう一度お試しください。");
	}
	return converted.flatMap((p) => [p.x, p.y, p.z]);
};

/**
 * 候補経路（フラット [x, y, z, ...]）を viewer に渡して仮設道路を作成する。
 * 既存の createRoadFromPointsWithAI を再利用し、道路の作成完了まで待つ。
 *
 * @param {number[]} pointsFlat プロジェクト座標のフラット配列
 * @param {number|null} pointCloudAssetId 関連付ける点群アセットID（無ければ null）
 * @param {(id: string) => void} onCreated 作成後コールバック
 * @returns {Promise<void>} 作成完了で resolve、座標変換または viewer の作成失敗で reject
 */
export const renderPrototypeRoad = async (
	pointsFlat,
	pointCloudAssetId,
	onCreated,
) => {
	const converted = await convertPointsFlat(pointsFlat);
	await window.viewer.createRoadFromPointsWithAI(
		pointsFlat,
		converted,
		(id) => {
			if (onCreated) onCreated(id);
		},
		pointCloudAssetId,
	);
};
